import {ADDITIONAL_FROZEN_UNTIL_SECONDS, ImprovementStatus} from './ImprovementsStore.constants';
import {
  Improvement,
  ImprovementForUI,
  ImprovementForUIBought,
  ImprovementForUILock,
  ImprovementForUIUnlock,
  ImprovementStatic,
  ImprovementWithStaticProps,
  ImprovementsState,
} from './ImprovementsStore.types';
import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {ProjectDate} from '@/services/ProjectDate/ProjectDate';

import {Store} from '../Store';

export class ImprovementsStore extends Store<ImprovementsState> {
  constructor(private readonly projectDate: ProjectDate) {
    super({
      currentIncomePerHour: 0,
      improvements: [],
      improvementsStatic: [],
      improvementsForUI: [],
      detailImprovementId: null,
    });
  }

  public setCurrentIncomePerHour = (currentIncomePerHour: number): void => {
    this.setValue('currentIncomePerHour', currentIncomePerHour);
  };

  public setImprovements = (improvements: Improvement[]): void => {
    this.setValue('improvements', improvements);
    this.updateImprovementsForUI();
  };

  public setImprovementsStatic = (improvements: ImprovementStatic[]): void => {
    this.setValue('improvementsStatic', improvements);
    this.updateImprovementsForUI();
  };

  public updateImprovementsForUI = (): void => {
    const {improvements, improvementsStatic} = this.getState();

    const improvementsWithStaticProps = improvements.reduce<ImprovementWithStaticProps[]>((acc, improvement) => {
      const improvementStaticProps = improvementsStatic.find(({id}) => id === improvement.id);

      if (improvementStaticProps) {
        acc.push({...improvement, ...improvementStaticProps});
      }

      return acc;
    }, []);

    this.setValue(
      'improvementsForUI',
      this.sortImprovementsByRequiredTotalIncomePerHour(improvementsWithStaticProps).map((improvement) =>
        this.buildImprovementForUI(improvement),
      ),
    );
  };

  private buildImprovementForUI = (improvement: ImprovementWithStaticProps): ImprovementForUI => {
    const {currentIncomePerHour} = this.getState();
    const baseProps = {
      id: improvement.id,
      title: improvement.name,
      description: improvement.description,
      cost: improvement.upgradeCost,
    };

    if (currentIncomePerHour < improvement.requiredTotalIncomePerHour) {
      return {
        ...baseProps,
        status: ImprovementStatus.Lock,
        incomePerHour: improvement.nextLevelIncomePerHour,
        requiredTotalIncomePerHour: improvement.requiredTotalIncomePerHour,
      };
    }

    if (improvement.currentLevel === 0) {
      return {
        ...baseProps,
        status: ImprovementStatus.Unlock,
        incomePerHour: improvement.nextLevelIncomePerHour,
      };
    }

    let frozenUntil: string;
    if (improvement.frozenUntil && this.projectDate.isAfterNow(improvement.frozenUntil)) {
      // Если кликнуть на покупку сразу после завершения таймера, то улучшение не обновится,
      // так как бэк в это время еще думает, что улучшение заморожено.
      const frozenUntilDate = new Date(improvement.frozenUntil);
      frozenUntilDate.setSeconds(frozenUntilDate.getSeconds() + ADDITIONAL_FROZEN_UNTIL_SECONDS);
      frozenUntil = frozenUntilDate.toISOString();
    } else {
      // Если frozenUntil не пришло с бэка, то считаем, что заморозки нет
      frozenUntil = this.projectDate.date.toISOString();
    }

    return {
      ...baseProps,
      status: ImprovementStatus.Bought,
      incomePerHour: improvement.currentLevelIncomePerHour,
      nextIncomePerHour: improvement.nextLevelIncomePerHour,
      level: improvement.currentLevel,
      nextLevel: improvement.currentLevel + 1,
      incomePerHourDifference: improvement.nextLevelIncomePerHour - improvement.currentLevelIncomePerHour,
      frozenUntil,
    };
  };

  public setDetailImprovementId = (id: string | null): void => {
    this.setValue('detailImprovementId', id);
  };

  public setImprovementFrozenUntilNowById = (id: string): void => {
    const {improvements} = this.getState();

    const updatedImprovements = improvements.map((improvement) => {
      if (improvement.id === id) {
        return {
          ...improvement,
          frozenUntil: this.projectDate.date.toISOString(),
        };
      }

      return improvement;
    });

    this.setImprovements(updatedImprovements);
  };

  public getImprovementsForUI = (): ImprovementForUI[] => {
    return this.getState().improvementsForUI;
  };

  public getDetailImprovementId = (): string | null => {
    return this.getState().detailImprovementId;
  };

  public getDetailImprovement = (): ImprovementForUI | null => {
    const detailImprovementId = this.getState().detailImprovementId;

    if (!detailImprovementId) {
      return null;
    }

    return this.getImprovementsForUI().find((improvement) => improvement.id === detailImprovementId) || null;
  };

  public isFirstImprovement = (id: string): boolean => {
    const {improvementsForUI} = this.getState();

    return improvementsForUI[0].id === id;
  };

  public hasBoughtImprovement = (): boolean => {
    const {improvementsForUI} = this.getState();

    return improvementsForUI.some((improvement) => improvement.status === ImprovementStatus.Bought);
  };

  public static isImprovementStatusLock = (improvement: ImprovementForUI): improvement is ImprovementForUILock => {
    return improvement.status === ImprovementStatus.Lock;
  };

  public static isImprovementStatusUnlock = (improvement: ImprovementForUI): improvement is ImprovementForUIUnlock => {
    return improvement.status === ImprovementStatus.Unlock;
  };

  public static isImprovementStatusBought = (improvement: ImprovementForUI): improvement is ImprovementForUIBought => {
    return improvement.status === ImprovementStatus.Bought;
  };

  public static isLockByCost = (playerValue: number, improvementCost: number): boolean => {
    return playerValue < improvementCost;
  };

  public isLockByFrozenUntil = (frozenUntil: string): boolean => {
    return this.projectDate.isAfterNow(frozenUntil);
  };

  public static calculateCostDifference = (playerValue: number, improvementCost: number): number => {
    return improvementCost - playerValue;
  };

  public static calculateAntiFrozenCost = (defaultCost: number): number => {
    return defaultCost * 2;
  };

  private sortImprovementsByRequiredTotalIncomePerHour = (
    improvements: ImprovementWithStaticProps[],
  ): ImprovementWithStaticProps[] => {
    return improvements.sort((a, b) => a.requiredTotalIncomePerHour - b.requiredTotalIncomePerHour);
  };
}

injected(ImprovementsStore, DI_TOKENS.projectDate);
